/** Login: email → "check your inbox" with code entry; the link from the email lands on /login/verify. */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { DemoBackend, type DemoEmail } from '../backend/demo.js';
import { ApiError } from '../backend/types.js';
import { Link, useRouter } from '../router.js';
import { useT, type T } from '../i18n.js';
import { useAsync, useBackend, useSession } from '../state.js';
import { LangSwitch, SiteFooter } from '../components/Footer.js';

function AuthFrame({ children }: { children: React.ReactNode }) {
  const { t } = useT();
  return (
    <div className="auth">
      <div className="top">
        <img src={`${import.meta.env.BASE_URL}logo.svg`} alt="HOTPOT CMS" width={304} height={68} />
        <LangSwitch />
      </div>
      <main className="card">
        {children}
        <p className="hint" style={{ marginTop: 24 }}>
          <Link to="/privacy">{t('privacy')}</Link>
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}

export function errorText(e: unknown, t: T): string {
  if (e instanceof ApiError) {
    const map: Record<string, Parameters<T>[0]> = {
      invalid_email: 'invalidEmail',
      invalid_code: 'invalidCode',
      too_many_attempts: 'tooManyAttempts',
      too_many_requests: 'tooManyRequests',
      link_expired: 'linkExpired',
      draft_conflict: 'draftConflict',
    };
    if (map[e.code]) return t(map[e.code]!);
    return e.message;
  }
  return (e as Error)?.message ?? String(e);
}

export function Login() {
  const { t } = useT();
  const backend = useBackend();
  const { setMe } = useSession();
  const { loc, navigate } = useRouter();
  const [email, setEmail] = useState(() => loc.query.get('email') ?? '');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const codeRef = useRef<HTMLInputElement>(null);
  const operator = useAsync(() => backend.legal(), []);

  useEffect(() => {
    if (step === 'code') codeRef.current?.focus();
  }, [step]);

  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    setError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setError(t('invalidEmail'));
    setBusy(true);
    try {
      await backend.requestLogin(email.trim());
      setStep('code');
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const me = await backend.verifyCode(email.trim(), code);
      setMe(me);
      navigate('/', { replace: true });
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  const operatorText = operator.data?.name || 'Hotpot Studio';

  return (
    <AuthFrame>
      {loc.query.get('out') && step === 'email' && <p className="muted">{t('loggedOut')}</p>}
      {step === 'email' ? (
        <>
          <h1>{t('loginTitle')}</h1>
          <p>{t('loginIntro')}</p>
          <form onSubmit={send} noValidate>
            <label className="sr-only" htmlFor="email">
              {t('email')}
            </label>
            <input id="email" className="input" type="email" autoComplete="email" inputMode="email" placeholder={t('email')} value={email} onChange={(e) => setEmail(e.target.value)} autoFocus aria-invalid={!!error} aria-describedby="login-error" />
            {error && (
              <p className="error-text" id="login-error" role="alert">
                {error}
              </p>
            )}
            <div>
              <button className="btn big" disabled={busy}>
                {busy ? t('sending') : t('sendLink')}
              </button>
            </div>
            <p className="hint" style={{ marginTop: 8 }}>
              {t('loginPrivacy', { operator: operatorText })}
            </p>
          </form>
        </>
      ) : (
        <>
          <h1>{t('checkInbox')}</h1>
          <p>{t('checkInboxText', { email: email.trim() })}</p>
          <form onSubmit={verify}>
            <label htmlFor="code" className="small muted">
              {t('code')}
            </label>
            <input
              id="code"
              ref={codeRef}
              className="input code-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              aria-invalid={!!error}
            />
            {error && (
              <p className="error-text" role="alert">
                {error}
              </p>
            )}
            <div style={{ display: 'flex', gap: 28, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="btn big" disabled={busy || code.length !== 6}>
                {t('logIn')}
              </button>
              <button type="button" className="link-btn" onClick={() => send()}>
                {t('resend')}
              </button>
              <button type="button" className="link-btn" onClick={() => setStep('email')}>
                {t('useOtherEmail')}
              </button>
            </div>
          </form>
        </>
      )}
      {backend.demo && <DemoHelp backend={backend as DemoBackend} onPick={(e) => setEmail(e)} />}
    </AuthFrame>
  );
}

/** Demo only: the "emails" the real CMS would send, and one-click sample people. */
function DemoHelp({ backend, onPick }: { backend: DemoBackend; onPick: (email: string) => void }) {
  const { lang } = useT();
  const { setMe } = useSession();
  const { navigate } = useRouter();
  const [inbox, setInbox] = useState<DemoEmail[]>(backend.inbox);
  useEffect(() => backend.onInbox(() => setInbox([...backend.inbox])) as () => void, [backend]);
  const cs = lang === 'cs';
  const people = [
    { email: 'kristian@hotpot-studio.com', label: cs ? 'Hotpot Studio (všechny weby)' : 'Hotpot Studio (all sites)' },
    { email: 'david@smykverse.com', label: cs ? 'SMYK – správce' : 'SMYK owner' },
    { email: 'kacka@smykverse.com', label: cs ? 'SMYK – editorka' : 'SMYK editor' },
  ];
  return (
    <div className="notice pink" style={{ marginTop: 28 }}>
      <strong>{cs ? 'Demo' : 'Demo'}</strong> ·{' '}
      {cs
        ? 'Tady se nic neposílá. E-mail, který by přišel, se ukáže níže. Nebo se rovnou přihlaste jako:'
        : 'Nothing is sent here. The email you would get appears below. Or log straight in as:'}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '10px 0 4px' }}>
        {people.map((p) => (
          <span key={p.email} style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              className="link-btn ink"
              style={{ textDecoration: 'underline' }}
              onClick={async () => {
                setMe(await backend.loginAs(p.email));
                navigate('/', { replace: true });
              }}
            >
              {p.label}
            </button>
            <button className="link-btn small" onClick={() => onPick(p.email)}>
              {p.email}
            </button>
          </span>
        ))}
      </div>
      {inbox.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div className="small muted">{cs ? 'Demo schránka' : 'Demo inbox'}</div>
          {inbox.slice(0, 3).map((m) => (
            <div key={m.id} style={{ background: 'var(--panel)', borderTopLeftRadius: 16, padding: '12px 16px', marginTop: 8 }}>
              <div className="small muted">
                {m.to} · {m.at.toLocaleTimeString()}
              </div>
              <div style={{ margin: '4px 0 8px' }}>{m.subject}</div>
              <a className="btn" href={m.link}>
                {cs ? 'Přihlásit se' : 'Log in'}
              </a>{' '}
              <span className="small" style={{ marginLeft: 12 }}>
                {cs ? 'kód' : 'code'} <strong style={{ letterSpacing: 3 }}>{m.code}</strong>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Verify() {
  const { t } = useT();
  const backend = useBackend();
  const { setMe } = useSession();
  const { loc, navigate } = useRouter();
  const [error, setError] = useState('');
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    // The token is in the URL fragment, so it never reaches server logs or other sites through the Referer.
    const token = new URLSearchParams(loc.hash || window.location.hash.split('#').pop()).get('token') ?? loc.query.get('token');
    if (!token) {
      setError(t('linkExpired'));
      return;
    }
    backend.verifyToken(token).then(
      (me) => {
        setMe(me);
        try {
          window.history.replaceState(null, '', window.location.pathname + window.location.search + (import.meta.env.MODE === 'demo' ? '#/' : ''));
        } catch {
          /* ignore */
        }
        navigate('/', { replace: true, force: true });
      },
      (e) => setError(errorText(e, t)),
    );
  }, [backend, loc, navigate, setMe, t]);

  return (
    <AuthFrame>
      {error ? (
        <>
          <h1>{t('linkExpired')}</h1>
          <p>{t('linkExpiredText')}</p>
          <Link className="btn big" to="/login">
            {t('resend')}
          </Link>
        </>
      ) : (
        <h1 aria-live="polite">{t('verifying')}</h1>
      )}
    </AuthFrame>
  );
}
